/* eslint-disable prefer-const */
import { Pipe, PipeTransform } from '@angular/core';
import { LocaleService } from '@fleet/locale/locale-service/locale.service';
import { DateTime } from 'luxon';

const FULL_DATE_FORMAT = 'D hh:mm a ZZ';
const FULL_DATE_FORMAT_NO_ZONE = 'D hh:mm a';
const TIME_ONLY_FORMAT = 'hh:mm a';
const ZONE_FORMAT = 'ZZ';

@Pipe({
  name: 'searchTerms',
  standalone: false,
})
export class SearchTermsPipe implements PipeTransform {
  constructor(private localeService: LocaleService) {}

  transform(
    value: any,
    paramLabelMap?: any,
    combine?: boolean,
  ): [{ name: string; value: string; key: string; disabled?: boolean }] {
    let terms: [
      { name: string; value: string; key: string; disabled?: boolean },
    ] = [] as any;
    if (value) {
      const combinedDateRange =
        combine && value.fromDate != null && value.toDate != null
          ? this.buildCombinedDateRangeTerm(
              value.fromDate,
              value.toDate,
              paramLabelMap,
            )
          : null;
      const combinedRoutePlanDateRange =
        combine && value.dateFrom != null && value.dateTo != null
          ? this.buildCombinedDateRangeTerm(
              value.dateFrom,
              value.dateTo,
              paramLabelMap,
              'dateFrom',
              'dateTo',
            )
          : null;
      const combinedCreatedDateRange =
        combine && value.createdFrom != null && value.createdTo != null
          ? this.buildCombinedDateRangeTerm(
              value.createdFrom,
              value.createdTo,
              paramLabelMap,
              'createdFrom',
              'createdTo',
            )
          : null;

      Object.entries(value).forEach((element) => {
        if (
          element[1] !== null &&
          element[0] !== 'limit' &&
          element[0] !== 'offset' &&
          element[0] !== 'networkId'
        ) {
          let key = element[0];

          if (combinedDateRange && (key === 'fromDate' || key === 'toDate')) {
            return;
          }
          if (
            combinedRoutePlanDateRange &&
            (key === 'dateFrom' || key === 'dateTo')
          ) {
            return;
          }
          if (
            combinedCreatedDateRange &&
            (key === 'createdFrom' || key === 'createdTo')
          ) {
            return;
          }

          let treatedName = key.replace(/([A-Z])/g, ' $1');
          treatedName =
            treatedName.charAt(0).toUpperCase() + treatedName.slice(1);

          let value = element[1] as any;
          let chipDisabled = false;

          //test if there is a
          if (paramLabelMap && paramLabelMap[key]) {
            chipDisabled = paramLabelMap[key].disabled;

            if (paramLabelMap[key].label) {
              treatedName = paramLabelMap[key].label;
            }
            //Test if this is not a string
            if (value.length > 0 && typeof value !== 'string') {
              const labelValues = value
                .map((val: any) =>
                  paramLabelMap[key].find((s: any) => s.value === val),
                )
                .map((valuelabel: any) => valuelabel.label)
                .join(', ');
              value = labelValues;
              //value is array;
            } else {
              //

              if (paramLabelMap[key].dateFormat) {
                //llets use the key value as our format for this date

                if (paramLabelMap[key].fromFormat) {
                  value = DateTime.fromFormat(value, 'yyyyLLddHHmmss')
                    .setLocale(this.localeService.getLocale())
                    .toFormat(paramLabelMap[key].dateFormat);
                } else {
                  //is date

                  if (typeof value === 'string') {
                    value = DateTime.fromISO(value)
                      .setLocale(this.localeService.getLocale())
                      .toFormat(paramLabelMap[key].dateFormat);
                  } else {
                    value = value
                      .setLocale(this.localeService.getLocale())
                      .toFormat(paramLabelMap[key].dateFormat);
                  }
                }
              } else {
                //not a date

                const foundParam = paramLabelMap[key].find(
                  (s: any) => s.value === value,
                );

                if (foundParam) {
                  value = foundParam.label;
                }
              }
            }
          }
          let term = {
            name: treatedName,
            value: value,
            key: key,
            disabled: chipDisabled,
          };
          terms.push(term);
        }
      });

      if (combinedDateRange) {
        terms.push(combinedDateRange);
      }
      if (combinedRoutePlanDateRange) {
        terms.push(combinedRoutePlanDateRange);
      }
      if (combinedCreatedDateRange) {
        terms.push(combinedCreatedDateRange);
      }

      return terms;
    }

    return null;
  }

  private buildCombinedDateRangeTerm(
    fromRaw: unknown,
    toRaw: unknown,
    paramLabelMap?: any,
    fromKey = 'fromDate',
    toKey = 'toDate',
  ): { name: string; value: string; key: string; disabled?: boolean } | null {
    const fromConfig = paramLabelMap?.[fromKey];
    const toConfig = paramLabelMap?.[toKey];
    const fromDate = this.toDateTime(fromRaw, fromConfig);
    const toDate = this.toDateTime(toRaw, toConfig);

    if (!fromDate?.isValid || !toDate?.isValid) {
      return null;
    }

    const locale = this.localeService.getLocale();
    const dateOnly = fromConfig?.dateFormat === 'D';
    const sameOffset = fromDate.offset === toDate.offset;
    const fullFormat = dateOnly
      ? 'D'
      : sameOffset
        ? FULL_DATE_FORMAT_NO_ZONE
        : fromConfig?.dateFormat || toConfig?.dateFormat || FULL_DATE_FORMAT;

    const fromFormatted = fromDate.setLocale(locale).toFormat(fullFormat);
    const toFormatted =
      !dateOnly && fromDate.hasSame(toDate, 'day')
        ? toDate
            .setLocale(locale)
            .toFormat(sameOffset ? TIME_ONLY_FORMAT : `${TIME_ONLY_FORMAT} ZZ`)
        : toDate.setLocale(locale).toFormat(fullFormat);

    const range =
      fromDate.hasSame(toDate, 'day') && dateOnly
        ? fromFormatted
        : `${fromFormatted} - ${toFormatted}`;
    const value =
      dateOnly || !sameOffset
        ? range
        : `${range} ${fromDate.setLocale(locale).toFormat(ZONE_FORMAT)}`;

    return {
      name: fromConfig?.rangeLabel || 'Date',
      value,
      key: `${fromKey}-${toKey}`,
      disabled: fromConfig?.disabled || toConfig?.disabled,
    };
  }

  private toDateTime(value: unknown, labelConfig?: any): DateTime | null {
    if (value == null) {
      return null;
    }

    if (DateTime.isDateTime(value)) {
      return value as DateTime;
    }

    if (typeof value === 'string') {
      if (labelConfig?.fromFormat) {
        return DateTime.fromFormat(value, 'yyyyLLddHHmmss');
      }
      return DateTime.fromISO(value);
    }

    return null;
  }
}
